import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { FLOOR_OPTIONS } from "@/config/constants";
import type {
  FloorJumpSpeechBubbleData,
  IRoutingGraphBuilder,
  RouteResultForMapView,
  RoutingGraph,
  RoutingGraphNode,
  RoutingMode,
} from "../types/routing";
import { ManualRoutingGraphBuilder } from "./ManualRoutingGraphBuilder";
import {
  createNearestNodeOnLine,
  createRouteLineGeometry,
  findCoordsWithFloorForFacility,
  findNodeInGraphs,
  updateNearestNodeToDoorNode,
} from "../utils/routingUtils";
import { findPath } from "../utils/findPathUtils";
import { fetchScaledMapData } from "@/utils/mapDataUtils";
import { FaicilityCoordsForPathFind, LocationInput } from "../types/routingInterface";

// このクラスが、生成されたビルダークラスの唯一のインスタンスを保持する
export class RoutingService {
  private static instance: RoutingService | null = null;
  private graphBuilderInstance: IRoutingGraphBuilder;
  private scaledPolygonsMapCache: Map<string, FeatureCollection> | null = null;
  private scaledIconsMapCache: Map<string, FeatureCollection> | null = null;

  // 外部からのインスタンス化を禁止
  private constructor() {
    this.graphBuilderInstance = new ManualRoutingGraphBuilder();
  }

  //サービスの初期化
  public static initialize(): void {
    if (RoutingService.instance) {
      return;
    }

    RoutingService.instance = new RoutingService();
  }

  public static getRoutingServiceInstance(): RoutingService {
    if (!RoutingService.instance) {
      // エラーを投げて、不正な利用を強制的に防ぐ
      throw new Error(
        "RoutingServiceが初期化されていません。先にRoutingService.initialize()を呼び出してください。",
      );
    }

    return RoutingService.instance;
  }

  public async buildAll() {
    return this.graphBuilderInstance.buildAll();
  }

  /**
   * 経路探索に必要なグラフと施設データを先に用意しておく。
   * 初回の findPath でまとめて行うと最初の利用者を数百ms待たせるため、起動後の空き時間に呼ぶ
   */
  public async warmUp(): Promise<void> {
    await this.buildAll();
    await this.createRoutingPolygonData();
  }

  // --- 3. findPath の実装-
  public async findPath(
    start: LocationInput,
    goal: LocationInput,
    mode: RoutingMode = "default",
  ): Promise<RouteResultForMapView | null> {
    const graphMap = await this.buildAll();

    const { scaledPolygonsMap, scaledIconsMap } = await this.createRoutingPolygonData();
    //経路探索のスタートノード・ゴールノードを取得
    const startNode = await this.generateNodeFromLocationInput(
      start,
      scaledPolygonsMap,
      scaledIconsMap,
      graphMap,
    );
    const goalNode = await this.generateNodeFromLocationInput(
      goal,
      scaledPolygonsMap,
      scaledIconsMap,
      graphMap,
    );

    if (!startNode || !goalNode) {
      console.error("経路探索の出発地か目的地が見つかりませんでした。");
      return null;
    }

    //経路探索開始
    const routeResultForMapView = this.findPathFromNode(graphMap, startNode, goalNode, mode);

    return routeResultForMapView;
  }

  //locationInputの座標から最も近いノードを作成して返す
  private async generateNodeFromLocationInput(
    locationInput: LocationInput,
    scaledPolygonsMap: Map<string, FeatureCollection<Geometry, GeoJsonProperties>>,
    scaledIconsMap: Map<string, FeatureCollection<Geometry, GeoJsonProperties>>,
    graphMap: Map<string, RoutingGraph>,
  ): Promise<RoutingGraphNode | null> {
    let facilityCoords: FaicilityCoordsForPathFind | null;

    //座標を特定
    if (typeof locationInput === "string") {
      facilityCoords = await findCoordsWithFloorForFacility(
        locationInput,
        scaledPolygonsMap,
        scaledIconsMap,
        graphMap,
      );

      if (!facilityCoords) {
        console.error("施設IDから対応する座標を計算するのに失敗しました。");
        return null;
      }
    } else {
      facilityCoords = { ...locationInput, isDoor: false };
    }

    const graph = graphMap.get(facilityCoords.floorId);
    if (graph === undefined) {
      console.error("経路探索の出発地もしくは目的地の階層にグラフがありません。");
      return null;
    }

    let nearestNode = await createNearestNodeOnLine(
      graph,
      facilityCoords.position.x,
      facilityCoords.position.y,
    );
    if (!nearestNode) {
      console.error("出発地もしくは目的地の座標に対応するノードが発見できませんでした。");
      return null;
    }

    //対応するドアが存在する場合、ドア座標を開始or終端ノードにできるようにエッジ追加
    if (facilityCoords.isDoor) {
      nearestNode = updateNearestNodeToDoorNode(graph, nearestNode, facilityCoords);
    }

    return nearestNode;
  }

  private async findPathFromNode(
    routingGraphMap: Map<string, RoutingGraph>,
    startNode: RoutingGraphNode,
    goalNode: RoutingGraphNode,
    mode: RoutingMode = "default",
  ): Promise<RouteResultForMapView | null> {
    const routeResult = findPath(routingGraphMap, startNode, goalNode, mode);

    if (!routeResult) {
      return null;
    }

    // LineString geometryを生成（マップに描画される経路の線）
    const lineGeometry = createRouteLineGeometry(routeResult.nodes);

    //階段・エレベーターで表示するフロア変更の吹き出し用のデータ作成
    const nullFloorJumpData: FloorJumpSpeechBubbleData = {
      //これなくてもいいようにしたい
      floorIdGoingFrom: "null",
      floorIdGoingTo: "null",
      numberOfFloorGoingTo: 0,
      direction: "up",
      type: "jump",
      fromNodePosition: {
        x: -1,
        y: -1,
      },
      toNodePosition: {
        x: -1,
        y: -1,
      },
    };
    const floorJumpDatas: FloorJumpSpeechBubbleData[] | undefined = routeResult.edges
      .filter((edge) => {
        return edge.linkType;
      })
      .flatMap((linkEdge) => {
        const fromNode = findNodeInGraphs(routingGraphMap, linkEdge.from);
        if (!fromNode) {
          return [nullFloorJumpData];
        }
        const floorIdGoingFrom = fromNode.floorId;

        const toNode = findNodeInGraphs(routingGraphMap, linkEdge.to);
        if (!toNode) {
          return [nullFloorJumpData];
        }
        const floorIdGoingTo = toNode.floorId;
        const numberOfFloorGoingTo = toNode.floor;

        const numberOfFloorGoingFrom = fromNode.floor;
        const direction = numberOfFloorGoingTo - numberOfFloorGoingFrom > 0 ? "up" : "down";

        const fromNodePosition = {
          x: fromNode.position.x,
          y: fromNode.position.y,
        };

        const toNodePosition = {
          x: toNode.position.x,
          y: toNode.position.y,
        };

        const goData: FloorJumpSpeechBubbleData = {
          floorIdGoingFrom: floorIdGoingFrom,
          floorIdGoingTo: floorIdGoingTo,
          numberOfFloorGoingTo: numberOfFloorGoingTo,
          direction: direction,
          type: "jump",
          fromNodePosition: fromNodePosition,
          toNodePosition: toNodePosition,
        };

        const returnDirection = numberOfFloorGoingFrom - numberOfFloorGoingTo > 0 ? "up" : "down";
        const returnData: FloorJumpSpeechBubbleData = {
          floorIdGoingFrom: floorIdGoingTo,
          floorIdGoingTo: floorIdGoingFrom,
          numberOfFloorGoingTo: numberOfFloorGoingFrom,
          direction: returnDirection,
          type: "return",
          fromNodePosition: toNodePosition,
          toNodePosition: fromNodePosition,
        };

        return [goData, returnData];
      });

    const routeResultForMapView: RouteResultForMapView = {
      ...routeResult,
      lineGeometry: lineGeometry,
      floorJumpDatas: floorJumpDatas,
    };

    return routeResultForMapView;
  }

  async createRoutingPolygonData(): Promise<{
    scaledPolygonsMap: Map<string, FeatureCollection>;
    scaledIconsMap: Map<string, FeatureCollection>;
  }> {
    if (this.scaledPolygonsMapCache && this.scaledIconsMapCache) {
      return {
        scaledPolygonsMap: this.scaledPolygonsMapCache,
        scaledIconsMap: this.scaledIconsMapCache,
      };
    }

    const scaledPolygonsMap = new Map<string, FeatureCollection>();
    const scaledIconsMap = new Map<string, FeatureCollection>();
    const availableFloors = FLOOR_OPTIONS;
    const loadDataForNodeDetectionPromises = availableFloors.map(async (floorOption) => {
      const floorId = floorOption.id;
      const floorPath = floorOption.path;
      const { scaledPolygons, scaledIconsGeoJSON } = await fetchScaledMapData(floorPath);
      scaledPolygonsMap.set(floorId, scaledPolygons);
      scaledIconsMap.set(floorId, scaledIconsGeoJSON);
    });
    await Promise.all(loadDataForNodeDetectionPromises);

    //キャッシュの生成
    this.scaledPolygonsMapCache = scaledPolygonsMap;
    this.scaledIconsMapCache = scaledIconsMap;

    return {
      scaledPolygonsMap: scaledPolygonsMap,
      scaledIconsMap: scaledIconsMap,
    };
  }
}
